import { useEffect, useState } from "react";
import { X, Mail, Copy, Check, ExternalLink } from "lucide-react";

const SUPPORT_EMAIL = "unigateappinfo@gmail.com";

const PRESETS = {
  demo: {
    title: "Request a Demo",
    subtitle:
      "Send us a quick email and we'll set up a personalized walkthrough for your organization.",
    subject: "Request a UGatePass Demo",
    body:
      "Hi UGatePass Team,\n\nI'd like to request a demo of UGatePass for our organization.\n\nOrganization Name:\nIndustry (Apartment / School / Office / Hospital / Factory / Warehouse / Port / Event):\nNumber of Gates:\nContact Number:\nPreferred Time:\n\nThanks!",
  },
  contact: {
    title: "Contact UGatePass",
    subtitle:
      "Drop us a message and our team will get back to you shortly.",
    subject: "UGatePass — Contact Request",
    body:
      "Hi UGatePass Team,\n\nI'd like to know more about UGatePass.\n\nName:\nOrganization:\nMessage:\n\nThanks!",
  },
};

const CopyRow = ({ label, value, testid }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // Fallback for environments without clipboard API
      const ta = document.createElement("textarea");
      ta.value = value;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch {
        /* noop */
      }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-[#F5F8FC] p-3">
      <div className="flex items-center justify-between gap-3 mb-1.5">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
          {label}
        </div>
        <button
          type="button"
          onClick={onCopy}
          data-testid={`${testid}-copy`}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1F5FBF] hover:text-[#23395D] transition-colors"
        >
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5" /> Copied
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" /> Copy
            </>
          )}
        </button>
      </div>
      <div
        data-testid={`${testid}-value`}
        className="text-[13px] text-[#23395D] font-medium whitespace-pre-wrap break-words leading-relaxed max-h-40 overflow-y-auto"
      >
        {value}
      </div>
    </div>
  );
};

const EmailActionDialog = ({ open, onClose, type = "demo" }) => {
  const preset = PRESETS[type] || PRESETS.demo;
  const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    preset.subject
  )}&body=${encodeURIComponent(preset.body)}`;

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      data-testid="email-action-dialog"
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
    >
      {/* Backdrop */}
      <button
        type="button"
        data-testid="email-action-dialog-backdrop"
        aria-label="Close dialog"
        onClick={onClose}
        className="absolute inset-0 bg-[#23395D]/60 backdrop-blur-sm"
      />
      {/* Panel */}
      <div className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl border border-slate-200 max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="relative p-6 pb-4 border-b border-slate-100 bg-gradient-to-br from-[#1F5FBF] to-[#4CAF50] rounded-t-3xl sm:rounded-t-3xl text-white">
          <button
            type="button"
            onClick={onClose}
            data-testid="email-action-dialog-close"
            aria-label="Close"
            className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/15 border border-white/25 flex items-center justify-center">
              <Mail className="w-5 h-5" />
            </div>
            <div>
              <h3 data-testid="email-action-dialog-title" className="text-xl font-extrabold leading-tight">
                {preset.title}
              </h3>
              <p className="text-white/85 text-[13px] mt-0.5 leading-snug">
                {preset.subtitle}
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-3 overflow-y-auto">
          <CopyRow
            label="Send to"
            value={SUPPORT_EMAIL}
            testid="email-action-to"
          />
          <CopyRow
            label="Subject"
            value={preset.subject}
            testid="email-action-subject"
          />
          <CopyRow
            label="Message"
            value={preset.body}
            testid="email-action-body"
          />
          <p className="text-xs text-slate-500 leading-relaxed pt-1">
            Tip: tap <span className="font-semibold text-[#23395D]">Open in Email App</span> to
            launch your default mail client, or copy any field above and paste
            into your preferred email tool.
          </p>
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-5 border-t border-slate-100 bg-white rounded-b-3xl flex flex-col sm:flex-row gap-3 sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            data-testid="email-action-dialog-cancel"
            className="px-5 py-3 rounded-full text-sm font-semibold text-[#23395D] bg-[#F5F8FC] hover:bg-slate-100 transition-colors"
          >
            Close
          </button>
          <a
            href={mailto}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="email-action-open-mail-app"
            className="inline-flex justify-center items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-[#1F5FBF] to-[#4CAF50] text-white text-sm font-semibold shadow-sm hover:opacity-95 transition-opacity"
          >
            Open in Email App <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default EmailActionDialog;
